import React from 'react'
import PropTypes, { any } from 'prop-types'
import { useLocation } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { useTheme } from '../../../../../components/mui/Styles/index'
import LinkButton from '../../../../../components/LinkButton/link-button'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import Constants from '../../../../../constants/en.json'
import { downloadReport } from '../../../../../api/reporting/download-report'
import { inputFormattingConstants } from '../../../../../constants/input-formatting-constants'

export function ReportsTable({ reports, isFetchingReports, searchedDates }) {
  const theme = useTheme()
  const { state } = useLocation()
  const orderBy = state.orderBy
  const dateTimeFormat = inputFormattingConstants.dateFormats.MMDDYYYYHHmmA
  const filterByDescription = `${searchedDates.startDate} to ${searchedDates.endDate}`

  const handleReportLinkClick = async (row) => {
    downloadReport(row.reportLocation, row.reportName, orderBy)
  }

  const linkButtonStyle = {
    fontFamily: 'AtlasGroteskRegular',
    fontSize: '16px !important',
  }

  const gridStyle = {
    minHeight: '160px',
    height: isFetchingReports ? '400px' : 'auto',
    marginTop: '20px',
    backgroundColor: theme.palette.common.white,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '16px',
    },
    '& .MuiDataGrid-cell': {
      fontSize: '16px',
    },
  }

  const rows = reports.map((row) => ({
    id: row.reportName,
    reportName: row.reportName,
    creationDate: row.creationDate,
    reportLocation: row.reportLocation,
  }))

  const columns = [
    {
      field: 'reportName',
      headerName: Constants.reports.FILE_NAME,
      minWidth: 250,
      renderCell: (cellValues) => {
        const reportAttachment = cellValues.row.reportName
        return (
          <LinkButton
            handleLinkClick={() => {
              handleReportLinkClick(cellValues.row)
            }}
            sx={linkButtonStyle}
            linkText={reportAttachment}
          />
        )
      },
      flex: 0.09,
    },
    {
      field: 'creationDate',
      headerName: Constants.reports.DATE_GENERATED,
      minWidth: 200,
      valueGetter: (value) => value,
      valueFormatter: (value) =>
        value
          ? dayjs(value).format(dateTimeFormat).split('-').join('/')
          : Constants.reports.PENDING,
      headerAlign: 'right',
      align: 'right',
      flex: 0.08,
    },
  ]

  return (
    <DataGrid
      data-testid='displayReports'
      sx={gridStyle}
      rows={rows}
      columns={columns}
      loading={isFetchingReports}
      spacing={1}
      pageSizeOptions={[25, 50]}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
      }}
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
        noRowsOverlay: {
          filterByDescription: 'Date Period',
          noResultsValue: filterByDescription,
        },
      }}
      slots={{
        noRowsOverlay: NoResultsFoundTableOverlay,
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
      disableColumnFilter
      disableRowSelectionOnClick
    />
  )
}

ReportsTable.propTypes = {
  reports: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
  isFetchingReports: PropTypes.bool.isRequired,
  searchedDates: PropTypes.shape({
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
  }).isRequired,
}

export default ReportsTable
